/**
 * Form13_RemoteMonitoring.jsx
 * Collection: remote_monitoring_consents
 * Redirect: /forms/14-review-of-systems
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/remote-monitoring";
const NEXT    = "/forms/14-review-of-systems";

export default function RemoteMonitoring({ patientName="", patientAge="", practiceName="", onSubmit }) {
  const history=useHistory();
  const formRef=useRef(null);
  const sigRef=useRef(null);
  const [sigEmpty,setSigEmpty]=useState(true);
  const [submitting,setSubmitting]=useState(false);
  const [savedId,setSavedId]=useState(null);
  const [showToast,Toast]=useToast();

  const [form,setForm]=useState({
    printName:patientName, bestPhone:"",
    legalRep:"", legalRepSig:"",
    responsibleParty:patientName, relationship:"Self",
    sigDate:new Date().toISOString().split("T")[0],
  });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const getSig=()=>(!sigRef.current||sigRef.current.isEmpty())?null:sigRef.current.toDataURL("image/png");

  const buildDoc=()=>({
    formType:"remote_monitoring_consent",
    patient:{name:patientName, age:patientAge!==""?Number(patientAge):null},
    practiceName,
    enrollment:{printName:form.printName, bestPhone:form.bestPhone, legalRep:form.legalRep},
    authorization:{responsibleParty:form.responsibleParty,relationship:form.relationship,sigDate:form.sigDate||null,signatureDataURL:getSig()},
    meta:{submittedAt:new Date().toISOString(),formVersion:"1.0.0"},
  });

  const handleSubmit=async()=>{
    if(sigEmpty){showToast("Please sign before submitting.","error");return;}
    setSubmitting(true);
    try{
      const doc=buildDoc();
      const res=typeof onSubmit==="function"?await onSubmit(doc):await saveToMongo(doc,API_URL);
      if(res?._id)setSavedId(res._id);
      showToast("Enrolled! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    }catch(e){console.error(e);showToast("Save failed.","error");}
    finally{setSubmitting(false);}
  };
  const handleDownload=async()=>{
    try{await downloadPDF(formRef,`remote-monitoring-${patientName||"patient"}.pdf`);showToast("Downloaded!");}
    catch{showToast("PDF failed.","error");}
  };

  const practice=practiceName||"Just Heart Cardiovascular Group Inc.";

  return (
    <FormShell title="Remote Monitoring of My Medical Condition"
      subtitle={`Thank you for choosing ${practice} for your care.`}
      dbLabel="MongoDB · remote_monitoring_consents"
      patientName={patientName} patientAge={patientAge} savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">ℹ</span>Program Benefits</div>
        <div className="wf-policy">
          <ul>
            <li>We'll create a Care Plan custom-designed to meet your goals (you'll receive a copy).</li>
            <li>We'll do a full assessment of your health needs.</li>
            <li>Every month, we'll check in to make sure things are going well.</li>
            <li>You'll have access to a healthcare provider who can address your chronic conditions 24/7.</li>
            <li>We'll aim to address issues outside the office, resulting in fewer office visits and fewer hospital admissions.</li>
          </ul>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">✓</span>Consent</div>
        <div className="wf-policy">
          <ul>
            <li>We may share your information with your other providers electronically to keep all involved with your care up-to-date.</li>
            <li>Only one provider can provide this service to you during a 30-day period.</li>
            <li>You agree that <strong>{practice}</strong> will be that one provider.</li>
            <li>You can cancel or revoke this service at any time by talking to our staff.</li>
            <li>In many cases, the service is free without a copay. This service should help improve your care and reduce your overall healthcare costs.</li>
          </ul>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">2</span>Enrollment Information</div>
        <div className="wf-grid wf-grid-2">
          <Field label="Print Name">
            <input type="text" value={form.printName} onChange={e=>set("printName",e.target.value)} placeholder="Full name" />
          </Field>
          <Field label="Best Phone Number">
            <input type="tel" value={form.bestPhone} onChange={e=>set("bestPhone",e.target.value)} placeholder="(___) ___-____" />
          </Field>
          <Field label="Legal Rep / Power of Attorney (Optional)">
            <input type="text" value={form.legalRep} onChange={e=>set("legalRep",e.target.value)} placeholder="Name if applicable" />
          </Field>
        </div>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}
/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const RemoteMonitoringSchema = new Schema({
  formType:{type:String,default:"remote_monitoring_consent"},
  patient:{name:String,age:Number}, practiceName:String,
  enrollment:{printName:String,bestPhone:String,legalRep:String},
  authorization:{responsibleParty:String,relationship:String,sigDate:Date,signatureDataURL:String},
  meta:{submittedAt:Date,formVersion:String},
},{timestamps:true,collection:"remote_monitoring_consents"});
*/
